import * as Yup from 'yup';

export interface InvoiceFormProduct {
  // Client-only identity for a single line item. The same catalog product may
  // appear on an invoice more than once, so the table, the edit drawer and the
  // delete action all key off this rather than `productId`. Never sent to the
  // API (`mapFormToApi` whitelists the fields it posts).
  lineId?: string;
  id?: string; // invoice-product record ID (cleared in duplicate mode)
  trackingId?: string; // used by the API to match existing products on update
  productId: string; // catalog product ID (required by API)
  productName: string; // display name (populated from API response)
  manufacturerName?: string; // display-only, populated from the product catalog
  custom: boolean; // required by API
  totalQuantityValue: number;
  totalQuantityUnit: string;
  totalCostValue: number;
  // Catalog density sent to the API for the invoice service's unit math
  // (FR-095). A missing/zero density makes create/edit 500, so the payload
  // floors it to 1.
  productDensity?: number;
  // Price per unit at full precision, and the unit it is expressed in — which
  // may differ from totalQuantityUnit (invoiced in gallons, priced per quart).
  // Held on the line rather than re-derived from totalCostValue ÷
  // totalQuantityValue at save time, because that division loses the unit and
  // cannot recover a rate for a line whose quantity is zero. Optional because
  // invoices saved before this existed carry neither.
  priceValue?: number;
  priceUnit?: string;
  // Whether this line's price should become the product's list price. Resolved
  // only when the whole invoice is saved, and never sent to the invoice API —
  // `mapFormToApi` whitelists what it posts.
  saveAsNewListPrice?: boolean;
  // The product's stored package weight, carried only so the list price save can
  // echo it back. That endpoint assigns the column from the request
  // unconditionally, so on the Java service an absent value writes NULL and
  // wipes it. Never sent to the invoice API.
  packageWeight?: number;
}

export interface InvoiceFormValues {
  invoiceNumber: string;
  vendor: string;
  invoiceDate: Date | null;
  invoiceDueDate: Date | null;
  cropSeason: string[];
  tagIds: string[];
  notes: string;
  invoiceProducts: InvoiceFormProduct[];
}

export const initialInvoiceValues: InvoiceFormValues = {
  invoiceNumber: '',
  vendor: '',
  invoiceDate: new Date(),
  invoiceDueDate: null,
  cropSeason: [],
  tagIds: [],
  notes: '',
  invoiceProducts: []
};

export const initialDetailsValues = {
  invoiceNumber: initialInvoiceValues.invoiceNumber,
  vendor: initialInvoiceValues.vendor,
  invoiceDate: initialInvoiceValues.invoiceDate,
  invoiceDueDate: initialInvoiceValues.invoiceDueDate,
  cropSeason: initialInvoiceValues.cropSeason,
  tagIds: initialInvoiceValues.tagIds,
  notes: initialInvoiceValues.notes
};

export const initialProductsValues = {
  invoiceProducts: initialInvoiceValues.invoiceProducts
};

export const invoiceValidationSchema = Yup.object().shape({
  invoiceNumber: Yup.string()
    .trim()
    .required('Invoice number is required'),
  invoiceDate: Yup.date()
    .nullable()
    .required('Invoice date is required'),
  invoiceDueDate: Yup.date().nullable(),
  vendor: Yup.string(),
  cropSeason: Yup.array().of(Yup.mixed()),
  tagIds: Yup.array().of(Yup.mixed()),
  notes: Yup.string().max(1000, 'Notes cannot exceed 1000 characters'),
  invoiceProducts: Yup.array()
    .min(1, 'At least one product is required')
    .required()
});

export default {
  initialValues: initialInvoiceValues,
  validationSchema: invoiceValidationSchema
};
